import React from 'react';
import { ArchiveIcon, BoxesIcon, CircleDollarSignIcon, ClipboardListIcon, LoaderCircleIcon, PackageCheckIcon, TrendingUpIcon } from 'lucide-react';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
type SummaryMetric = {
  label: string;
  value: string;
  detail: string;
};
const onePeopleSummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '189',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '83',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '80',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '1,902',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '1,024',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '1,019',
  detail: 'Units'
}];
const emptySummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '0',
  detail: 'Units'
}];
const onePeopleIncomingBatches = [{
  arrived: 'Feb 5, 2026',
  name: 'Spring overstock',
  expected: 1365,
  received: 1267,
  progress: 92.8
}, {
  arrived: 'Dec 6, 2025',
  name: 'Assembly part 4',
  expected: 7,
  received: 7,
  progress: 100
}, {
  arrived: 'Aug 30, 2025',
  name: 'August returns',
  expected: 337,
  received: 335,
  progress: 99.4
}, {
  arrived: 'Jul 10, 2025',
  name: 'July overstock',
  expected: 16,
  received: 16,
  progress: 100
}, {
  arrived: 'May 2, 2025',
  name: 'May overstock',
  expected: 258,
  received: 258,
  progress: 100
}];
const eddySummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '320',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '86',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '82',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '2,981',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '682',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '669',
  detail: 'Units'
}];
const eddyIncomingBatches = [{
  arrived: 'Nov 4, 2026',
  name: 'Eddy — January 3PL transfer',
  expected: 85,
  received: 86,
  progress: 100
}, {
  arrived: 'Jan 28, 2026',
  name: 'Eddy Samples',
  expected: 0,
  received: 0,
  progress: 0
}, {
  arrived: 'Dec 16, 2025',
  name: 'Eddy — December 2025 3PL transfer',
  expected: 1103,
  received: 1105,
  progress: 100
}, {
  arrived: 'Nov 21, 2025',
  name: 'Eddy 3PL transfer',
  expected: 118,
  received: 120,
  progress: 100
}, {
  arrived: 'Oct 10, 2025',
  name: 'Eddy 3PL Transfer',
  expected: 87,
  received: 86,
  progress: 100
}, {
  arrived: 'May 22, 2025',
  name: 'Eddy — TJX Overflow',
  expected: 0,
  received: 223,
  progress: 97.4
}, {
  arrived: 'May 19, 2025',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 845,
  progress: 99.7
}, {
  arrived: 'Sep 30, 2024',
  name: 'Eddy — October 3PL Transfer',
  expected: 22,
  received: 25,
  progress: 100
}, {
  arrived: 'Aug 13, 2024',
  name: 'Eddy — Fall 2024',
  expected: 168,
  received: 169,
  progress: 100
}, {
  arrived: 'Apr 8, 2024',
  name: 'Eddy April 2024',
  expected: 0,
  received: 0,
  progress: 100
}, {
  arrived: 'Jan 18, 2024',
  name: 'Eddy — Jan 2024',
  expected: 5,
  received: 0,
  progress: 100
}, {
  arrived: 'Dec 20, 1905',
  name: 'Eddy — 3PL transfer old',
  expected: 0,
  received: 183,
  progress: 100
}, {
  arrived: '—',
  name: 'Eddy — June TRX',
  expected: 139,
  received: 139,
  progress: 98.1
}, {
  arrived: '—',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 0,
  progress: 0
}];
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function InventoryDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const hasOnePeopleData = selectedOwner === '1 People';
  const hasEddyData = selectedOwner === 'Eddy';
  const isNisolo = selectedOwner === 'Nisolo';
  const {
    inventory: nisolo,
    loading: nisoloLoading,
    error: nisoloError
  } = useNisoloProducts(isNisolo);
  const nisoloMetrics: SummaryMetric[] = nisolo ? [{
    label: 'Received products',
    value: nisolo.totalProducts.toLocaleString('en-US'),
    detail: 'Products'
  }, {
    label: 'Available for sale products',
    value: nisolo.products.filter(product => product.units > 0).length.toLocaleString('en-US'),
    detail: 'Products'
  }, {
    label: 'Live products',
    value: nisolo.liveProducts.toLocaleString('en-US'),
    detail: 'Products'
  }, {
    label: 'Received units',
    value: nisolo.totalUnits.toLocaleString('en-US'),
    detail: 'Units'
  }, {
    label: 'Available for sale units',
    value: nisolo.totalUnits.toLocaleString('en-US'),
    detail: 'Units'
  }, {
    label: 'Live units',
    value: nisolo.liveUnits.toLocaleString('en-US'),
    detail: 'Units'
  }] : emptySummaryMetrics;
  const summaryMetrics = isNisolo ? nisoloMetrics : hasOnePeopleData ? onePeopleSummaryMetrics : hasEddyData ? eddySummaryMetrics : emptySummaryMetrics;
  const incomingBatches = hasOnePeopleData ? onePeopleIncomingBatches : hasEddyData ? eddyIncomingBatches : [];
  const totalMsrp = isNisolo ? currency(nisolo?.totalMsrp ?? 0) : hasOnePeopleData ? '$130,993' : hasEddyData ? '$321,656' : '$0';
  const maxOpportunity = isNisolo ? currency((nisolo?.totalMsrp ?? 0) * 0.39) : hasOnePeopleData ? '$65,437' : hasEddyData ? '$160,828' : '$0';
  const totalReceivedValue = isNisolo ? currency(nisolo?.totalRetail ?? 0) : hasOnePeopleData ? '$130,993' : hasEddyData ? '$320,582' : '$0';
  const overstockDetail = isNisolo ? `${(nisolo?.totalUnits ?? 0).toLocaleString('en-US')} units across ${(nisolo?.totalProducts ?? 0).toLocaleString('en-US')} products` : hasOnePeopleData ? '1,902 units across 189 products' : hasEddyData ? '2,981 units across 320 products' : '0 units across 0 products';
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="inventory-heading" data-id="element-910" data-mp="3mklcx9awogw7">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-911" data-mp="zcxblwxyp118">
        <div data-id="element-912" data-mp="uc9jf7xzye5f">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-913" data-mp="ubrhbuonl5pl">
            <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-914" data-mp="qdtsisysolmq" />
            Inventory
          </span>
          <h1 id="inventory-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-915" data-mp="lezavuckd347">
            Inventory dashboard
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-916" data-mp="1e1ts8ie33mdg">
            A clear view of inventory received, prepared for sale, and currently live across your approved recovery channels.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-917" data-mp="21qw8pmwzf96d">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-918" data-mp="1wz4r41vt0dsd" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <section className="mt-8 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" aria-label="Inventory overview" data-id="element-919" data-mp="24d5d78kdkb5w">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="summary-heading" data-id="element-920" data-mp="29chyhovlla6d">
          <div className="flex items-center gap-2" data-id="element-921" data-mp="2iatxofaj5gaf">
            <BoxesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-922" data-mp="2icn0tklxpido" />
            <h2 id="summary-heading" className="text-lg font-semibold" data-id="element-923" data-mp="2n952ucpvr2mb">Inventory summary</h2>
          </div>
          <div className="mt-6 grid gap-x-5 gap-y-6 sm:grid-cols-3" data-id="element-924" data-mp="2s7yhi8gwkzo2">
            {summaryMetrics.map((metric, index) => <div key={metric.label} className={`border-[#BB5E40]/25 pl-4 ${index % 3 === 0 ? 'border-l-0 pl-0' : 'border-l-2'} ${index >= 3 ? 'border-t pt-5 sm:border-t-0 sm:pt-0' : ''}`} data-id="element-925" data-mp="2x22r6zhjzlsu" data-mpi={index}>
                <p className="text-sm text-ink/55" data-id="element-926" data-mp="362heu5hzpczi">{metric.label}</p>
                <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-927" data-mp="3b0soog8abnhq">{metric.value}</p>
                <p className="mt-1 text-xs text-ink/50" data-id="element-928" data-mp="3qoego7pz8j">{metric.detail}</p>
              </div>)}
          </div>
        </article>

        <article className="rounded-3xl border border-ink/10 bg-forest p-5 text-cream shadow-sm sm:p-7" aria-labelledby="opportunity-heading" data-id="element-929" data-mp="a3hjajgoqg95">
          <div className="flex items-center gap-2 text-cream/80" data-id="element-930" data-mp="55fztxfckex5">
            <CircleDollarSignIcon className="h-4 w-4" aria-hidden="true" data-id="element-931" data-mp="3djye4gvvsqp7" />
            <h2 id="opportunity-heading" className="text-lg font-semibold text-cream" data-id="element-932" data-mp="3dhvfj8gnxp8v">Recovery value</h2>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5" data-id="element-933" data-mp="3n9rgcra4vtgs">
            <div data-id="element-934" data-mp="3nbc8ybcw12c0">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-935" data-mp="3nbufq1x6eyy6">Total MSRP</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-936" data-mp="3ihge3hrx7vl2">{totalMsrp}</p>
            </div>
            <div data-id="element-937" data-mp="2puvmqhbg3jhi">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-938" data-mp="2pvf5u1cu0dx4">Max opportunity</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-939" data-mp="15bjtamgrdaz5">{maxOpportunity}</p>
            </div>
          </div>
          <p className="mt-5 border-t border-cream/15 pt-4 text-sm leading-relaxed text-cream/70" data-id="element-940" data-mp="1a5e9vh12h9u0">Based on inventory that has been received and evaluated for recovery.</p>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[0.78fr_1.22fr]" data-id="element-941" data-mp="10bf8wlcqbw3n">
        <article className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="overstock-heading" data-id="element-942" data-mp="1o3m43h6pxx1h">
          <div className="flex items-center gap-2" data-id="element-943" data-mp="1t03170vwblqp">
            <PackageCheckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-944" data-mp="1f364wjx0dc2f" />
            <h2 id="overstock-heading" className="text-base font-semibold" data-id="element-945" data-mp="1j3gh4ebe8ypq">Overstock</h2>
          </div>
          <p className="mt-5 text-sm text-ink/55" data-id="element-946" data-mp="3mzlovx3dytp">Total received value</p>
          <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-ink" data-id="element-947" data-mp="7oumlzb42a6y">{totalReceivedValue}</p>
          <p className="mt-1 text-sm text-ink/55" data-id="element-948" data-mp="15wude29zt500">{overstockDetail}</p>
          <div className="mt-6 rounded-xl bg-[#BB5E40]/5 p-4" data-id="element-949" data-mp="15zoylw0tytuh">
            <div className="flex items-start gap-2" data-id="element-950" data-mp="1av4c2muxenqd">
              <TrendingUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-951" data-mp="x5ak3vo4jncf" />
              <p className="text-sm leading-relaxed text-ink/65" data-id="element-952" data-mp="116e2aotn9vgt">Inventory moves into recovery once it is received, checked in, and matched to your current channel and pricing rules.</p>
            </div>
          </div>
        </article>

        {isNisolo ? <NisoloProductTable products={nisolo?.products ?? []} loading={nisoloLoading} error={nisoloError} data-id="element-953" data-mp="s7isl2re638r" /> : <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="incoming-heading" data-id="element-954" data-mp="e7r9wopxjyof">
          <div className="flex items-center gap-2 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-955" data-mp="i9l6akt3egis">
            <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-956" data-mp="28i37yvdsnrn7" />
            <h2 id="incoming-heading" className="text-base font-semibold" data-id="element-957" data-mp="2ckzwt3txgjzl">Inventory submissions</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-958" data-mp="w374bmxx2z9">
            <table className="w-full min-w-[690px] text-left text-sm" data-id="element-959" data-mp="3t31ews6eff5u">
              <thead className="bg-cream/65 text-xs text-ink/50" data-id="element-960" data-mp="anz5lnsxuv40">
                <tr data-id="element-961" data-mp="apk3nnegow6m">
                  <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-962" data-mp="5xisy5wxzdip">Arrived</th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-963" data-mp="5vpkqm6okl8l">Batch</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-964" data-mp="kklqgcl9lm61">Expected units</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-965" data-mp="fn2drh47thg7">Net received</th>
                  <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-966" data-mp="tmbs0b8v21hj">Receipt progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-967" data-mp="2ve80td3n8x8m">
                {incomingBatches.length > 0 ? incomingBatches.map(batch => <tr key={batch.name} data-id="element-968" data-mp="2qgonqgiomqi9">
                    <td className="whitespace-nowrap px-5 py-4 text-ink/60 sm:px-6" data-id="element-969" data-mp="2qhrfsrrqqyzr">{batch.arrived}</td>
                    <th scope="row" className="px-3 py-4 font-medium text-ink" data-id="element-970" data-mp="zbbpdy7ye2e0">{batch.name}</th>
                    <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-971" data-mp="zd69597hnogi">{batch.expected.toLocaleString('en-US')}</td>
                    <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-972" data-mp="18cjdjngr9mud">{batch.received.toLocaleString('en-US')}</td>
                    <td className="px-5 py-4 sm:px-6" data-id="element-973" data-mp="1dal4svoc4sgx">
                      <div className="flex min-w-36 items-center gap-2" data-id="element-974" data-mp="1i9nzbm6ovgan">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/8" data-id="element-975" data-mp="1i7uso6w2sw2n">
                          <div className="h-full rounded-full bg-[#BB5E40]" style={{
                        width: `${batch.progress}%`
                      }} data-id="element-976" data-mp="1n3a3sr2p8dvd" />
                        </div>
                        <span className="w-10 text-right text-xs tabular-nums text-ink/55" data-id="element-977" data-mp="1w2nefn2k78ho">{batch.progress}%</span>
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-978" data-mp="3ljiyzif1lo9e">
                    <td colSpan={5} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-979" data-mp="3lk14l5nlhetz">Select an owner to view inventory submissions.</td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </article>}
      </section>
    </div>;
}
function NisoloProductTable({
  products,
  loading,
  error
}: {
  products: Array<{
    handle: string;
    title: string;
    type: string;
    status: string;
    image: string;
    variants: number;
    units: number;
    price: number;
    sizes: string[];
  }>;
  loading: boolean;
  error: string | null;
}) {
  return <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="nisolo-products-heading" data-id="element-980" data-mp="3vfvtgiiew657">
      <div className="flex items-center justify-between gap-3 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-981" data-mp="3qhkiu5kalrlx">
        <div className="flex items-center gap-2" data-id="element-982" data-mp="rtx1bkemrxtk">
          <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-983" data-mp="n0u492ana68m" />
          <h2 id="nisolo-products-heading" className="text-base font-semibold" data-id="element-984" data-mp="6l37aygpe1t3">Product inventory</h2>
        </div>
        <span className="text-xs text-ink/50" data-id="element-985" data-mp="bkxvgu93gdag">{products.length.toLocaleString('en-US')} products</span>
      </div>

      {loading && <div className="flex items-center justify-center gap-2 px-5 py-14 text-sm text-ink/55" data-id="element-986" data-mp="3ts56t3kfm0eb">
          <LoaderCircleIcon className="h-4 w-4 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-987" data-mp="2k9js676ap0q" />
          Loading Nisolo product inventory…
        </div>}

      {error && !loading && <p className="px-5 py-14 text-center text-sm text-ink/55 sm:px-6" data-id="element-988" data-mp="uanszp48rd62">{error}</p>}

      {!loading && !error && <div className="max-h-[520px] overflow-auto" data-id="element-989" data-mp="ghy2p8hojm35">
          <table className="w-full min-w-[640px] text-left text-sm" data-id="element-990" data-mp="lhkhhmd5pae2">
            <thead className="sticky top-0 bg-cream/95 text-xs text-ink/50 backdrop-blur" data-id="element-991" data-mp="197wmaod3ur0f">
              <tr data-id="element-992" data-mp="1dywjluq4zb3g">
                <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-993" data-mp="1dzem393e6ljt">Product</th>
                <th scope="col" className="px-3 py-3 font-semibold" data-id="element-994" data-mp="1ndgehu9fbm0p">Type</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-995" data-mp="1jcd1dfirg436">Variants</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-996" data-mp="1jak2imee2o5q">Units</th>
                <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-997" data-mp="1x5vw2qgt68l2">Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10" data-id="element-998" data-mp="14ekmmpl1fjq1">
              {products.map(product => <tr key={product.handle} data-id="element-999" data-mp="10e0phm0gsg3w">
                  <th scope="row" className="px-5 py-4 text-left font-medium text-ink sm:px-6" data-id="element-1000" data-mp="10f3grv1p8kkc">
                    <span className="flex items-center gap-3" data-id="element-1001" data-mp="1eeuzua72or57">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-ink/10 bg-cream" data-id="element-1002" data-mp="19dnqi6pa7jwf">
                        {product.image ? <img src={product.image} alt="" loading="lazy" className="h-full w-full object-cover" data-id="element-1003" data-mp="2ktvnztpuw518" /> : <PackageCheckIcon className="h-4 w-4 text-ink/25" aria-hidden="true" data-id="element-1004" data-mp="2sts79ixwd1mw" />}
                      </span>
                      <span className="min-w-0" data-id="element-1005" data-mp="31u89kp2wm2q6">
                        <span className="block max-w-xs truncate" data-id="element-1006" data-mp="31vjb0i8vmye4">{product.title}</span>
                        <span className="mt-0.5 block text-xs font-normal text-ink/45" data-id="element-1007" data-mp="36tb3pehciojd">
                          {product.status.toLocaleLowerCase() === 'active' ? 'Active' : product.status || 'Draft'}
                          {product.sizes.length > 0 && ` · ${product.sizes.length} sizes`}
                        </span>
                      </span>
                    </span>
                  </th>
                  <td className="px-3 py-4 text-ink/65" data-id="element-1008" data-mp="2nwjlz9hgchhf">{product.type || '—'}</td>
                  <td className="px-3 py-4 text-right tabular-nums text-ink/65" data-id="element-1009" data-mp="1qejo625atbrh">{product.variants}</td>
                  <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1010" data-mp="1vdd0vscrgyql">{product.units.toLocaleString('en-US')}</td>
                  <td className="px-5 py-4 text-right tabular-nums text-ink/70 sm:px-6" data-id="element-1011" data-mp="1nt2c5ig7hre0">{product.price > 0 ? currency(product.price) : '—'}</td>
                </tr>)}
              {products.length === 0 && <tr data-id="element-1012" data-mp="18y4kkrsvt5xz"><td colSpan={5} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1013" data-mp="18y4j0nd8gxvv">No products found in the file.</td></tr>}
            </tbody>
          </table>
        </div>}
    </article>;
}